import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import BreakingNews from '@/assets/icons/cover-images/breaking-news-circle-variation.png';
import LogoWatermark from '@/assets/icons/cover-images/panda-logo-watermark.png';
import PandaWatermark from '@/assets/icons/cover-images/panda-watermark-circle.png';
import HandEmoji from '@/assets/icons/hand-emoji.png';
import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { distributeTextCanvas } from '@/utils/coverImages/textDistribution';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
  isAiGenerated?: boolean;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset, isAiGenerated }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariation = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          async () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var9');

            const imageDiv = document.createElement('div');
            imageDiv.classList.add('full-image-var9');
            imageDiv.style.backgroundImage = `url(${variation.imageSrc})`;
            imageHolder.appendChild(imageDiv);

            const watermark = createWatermarkTagImage(
              'actual-photo-var9',
              variation.watermarkTag,
              variation.watermarkPosition,
            );
            if (watermark) imageHolder.appendChild(watermark);

            const bottomText = document.createElement('div');

            // Wait for font to load before measuring text
            await document.fonts.ready;

            if (variation.circleImageSrc) {
              const circleImage = new Image();
              circleImage.src = variation.circleImageSrc;
              circleImage.classList.add('circle-image-var9');

              if (variation.imagePosition === 'left') {
                circleImage.classList.add('position-left');
              } else if (variation.imagePosition === 'custom') {
                circleImage.classList.add('position-custom');
                circleImage.style.right = variation.imageLeft
                  ? `${variation.imageLeft}px`
                  : '63px';
                circleImage.style.top = variation.imageTop
                  ? `${variation.imageTop}px`
                  : '-415px';
              }

              bottomText.appendChild(circleImage);
            }

            if (variation.text) {
              const bottomTextP = document.createElement('p');
              bottomText.classList.add('bottom-text-var9');
              let text = variation.text;
              if (variation.textBrakes) {
                bottomTextP.innerText = text;
              } else {
                text = distributeTextCanvas(text, {
                  maxWidth: 900,
                  fontFamily: 'SourceSansProBold',
                  minFontSize:
                    variation.textFontSize !== 0 ? variation.textFontSize : 60,
                });
                bottomTextP.innerHTML = text;
              }

              if (variation.textEmoji) {
                const textEmoji = new Image();
                textEmoji.classList.add('text-emoji-var9');
                textEmoji.src = HandEmoji;
                bottomTextP.append(textEmoji);
              }

              bottomText.appendChild(bottomTextP);
            }

            imageHolder.appendChild(bottomText);

            if (variation.textFontSize) {
              bottomText.style.fontSize = `${variation.textFontSize}px`;
            }

            if (variation.logoWatermark) {
              const logoWatermark = new Image();
              logoWatermark.classList.add('logo-watermark-var9');
              logoWatermark.src = LogoWatermark;
              imageHolder.appendChild(logoWatermark);
            }

            if (variation.textWatermark) {
              const pandaWatermark = new Image();
              pandaWatermark.classList.add('watermark-var9');
              pandaWatermark.src = PandaWatermark;
              imageHolder.appendChild(pandaWatermark);
            }

            if (variation.breakingNews) {
              const breakingNewsImage = new Image();
              breakingNewsImage.classList.add('breaking-news-var9');
              breakingNewsImage.src = BreakingNews;
              imageHolder.appendChild(breakingNewsImage);

              if (breakingNewsImage.decode) {
                try {
                  await breakingNewsImage.decode();
                } catch {}
              } else {
                await new Promise<void>((res) => {
                  if (breakingNewsImage.complete) return res();
                  breakingNewsImage.onload = () => res();
                  breakingNewsImage.onerror = () => res();
                });
              }
            }

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var9',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariation,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadImagesAsZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                  isAiGenerated={isAiGenerated}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
